import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { useAnalyticsFilter } from '../../context/AnalyticsFilterContext';
import { useTopBarLeftContent } from '../../components/context/TopBarLeftContentContext';
import { useMobileFilter } from '../../components/context/MobileFilterContext';
import { useGoogleAds, useMetaAds } from '../../hooks';
import { formatDate, calculateDateRange } from '../../utils/dateRangeUtils';
import EnhancedDateRangeSelector from './EnhancedDateRangeSelector';
import CollapsibleFilterGroup from '../CollapsibleFilterGroup';
import MobileFilterDrawer from '../ui/MobileFilterDrawer';

// Mismo selector de períodos que Analytics20 (para el popup)
const basePeriods = [
  { key: 'TODAY', label: 'Hoy', days: 0, granularity: 'daily' },
  { key: 'YESTERDAY', label: 'Ayer', days: 1, granularity: 'daily' },
  { key: '7D', label: 'Últimos 7 días', days: 7, granularity: 'daily' },
  { key: '14D', label: 'Últimos 14 días', days: 14, granularity: 'daily' },
  { key: '30D', label: 'Últimos 30 días', days: 30, granularity: 'daily' },
  { key: 'ESTE_MES', label: 'Este mes', days: 'current_month', granularity: 'daily' },
  { key: 'MES_PASADO', label: 'El mes pasado', days: 'last_month', granularity: 'daily' },
  { key: 'CUSTOM', label: 'Personalizado', days: null, granularity: 'daily' },
];

const formatGoogleCampaigns = (list) =>
  (list || []).map((c) => ({
    id: String(c?.id ?? ''),
    name: c?.name ?? '',
    platform: 'google',
  }));

const formatMetaCampaigns = (list) =>
  (list || []).map((c) => ({
    id: String(c?.id ?? ''),
    name: c?.name ?? '',
    platform: 'meta',
  }));

export default function AnalyticsStickyFilterBar() {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    selectedRange,
    setSelectedRange,
    platform,
    selectedCampaignIds,
    setSelectedCampaignIds,
    selectedConversions,
    setSelectedConversions,
    campaignsFromTable,
  } = useAnalyticsFilter();

  const { isConnected: isGoogleConnected, fetchCampaigns: fetchGoogleCampaigns, campaigns: googleCampaigns, activeAccount: activeGoogleAccount } = useGoogleAds();
  const { isConnected: isMetaConnected, fetchCampaigns: fetchMetaCampaigns, campaigns: metaCampaigns, activeAccount: activeMetaAccount } = useMetaAds();

  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [campaignsHovered, setCampaignsHovered] = useState(false);
  const [conversionsHovered, setConversionsHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const campaignsRef = useRef(null);
  const conversionsRef = useRef(null);
  const { registerContent, clearContent } = useTopBarLeftContent();
  const { isMobileFilterOpen, closeMobileFilter } = useMobileFilter();

  // Track window resize to update responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startDateObj = useMemo(() => (startDate ? new Date(startDate) : null), [startDate]);
  const endDateObj = useMemo(() => (endDate ? new Date(endDate) : null), [endDate]);

  // Períodos para el popup (con formattedLabel para CUSTOM)
  const periodsForModal = useMemo(() => {
    const customLabel =
      selectedRange === 'CUSTOM' && startDate && endDate
        ? `${formatDate(startDateObj, 'full-year-short')} - ${formatDate(endDateObj, 'full-year-short')}`
        : '';
    return basePeriods.map((p) =>
      p.key === 'CUSTOM' ? { ...p, formattedLabel: customLabel } : p
    );
  }, [selectedRange, startDate, endDate, startDateObj, endDateObj]);


  // Opciones de conversiones: Google (conversion_actions) + Meta (pixels existing_events)
  const googleConversionOptions = useMemo(() => {
    if (!isGoogleConnected || !activeGoogleAccount?.conversion_actions) return [];
    return (activeGoogleAccount.conversion_actions || [])
      .filter((action) => action && action.status === 'ENABLED')
      .map((action) => ({
        platform: 'google',
        id: String(action?.id ?? ''),
        name: action?.name || '',
      }));
  }, [isGoogleConnected, activeGoogleAccount]);

  const metaConversionOptions = useMemo(() => {
    if (!isMetaConnected || !activeMetaAccount?.pixels?.data) return [];
    return (activeMetaAccount.pixels.data || []).flatMap((pixel) =>
      (pixel?.existing_events || []).map((event) => ({
        platform: 'meta',
        id: `${pixel?.id ?? ''}_${event ?? ''}`,
        name: `${pixel?.name ?? ''} - ${event ?? ''}`,
      }))
    );
  }, [isMetaConnected, activeMetaAccount]);

  const conversionOptions = useMemo(() => {
    const list = [];
    if (platform === 'all' || platform === 'google') list.push(...googleConversionOptions);
    if (platform === 'all' || platform === 'meta') list.push(...metaConversionOptions);
    return list;
  }, [platform, googleConversionOptions, metaConversionOptions]);

  const conversionOptionsByPlatform = useMemo(() => {
    const google = conversionOptions.filter((c) => c.platform === 'google');
    const meta = conversionOptions.filter((c) => c.platform === 'meta');
    return { google, meta };
  }, [conversionOptions]);

  const isConversionSelected = useCallback(
    (item) =>
      (selectedConversions || []).some((s) => s.platform === item.platform && String(s.id) === String(item.id)),
    [selectedConversions]
  );

  const toggleConversion = useCallback(
    (item) => {
      setSelectedConversions((prev) => {
        const list = prev || [];
        const isSelected = list.some((s) => s.platform === item.platform && String(s.id) === String(item.id));
        return isSelected
          ? list.filter((s) => !(s.platform === item.platform && String(s.id) === String(item.id)))
          : [...list, item];
      });
    },
    [setSelectedConversions]
  );

  const clearConversions = useCallback(() => setSelectedConversions([]), [setSelectedConversions]);

  // Usar las mismas campañas que la tabla "Resultados por campaña" cuando estén disponibles
  const campaignOptions = useMemo(() => {
    if (campaignsFromTable && campaignsFromTable.length > 0) {
      const filtered = campaignsFromTable.filter(
        (c) => platform === 'all' || c.platform === platform
      );
      return filtered.map((c) => ({
        ...c,
        label: `${c.name} (${c.platform === 'google' ? 'Google' : 'Meta'})`,
      }));
    }
    const list = [];
    if (platform === 'all' || platform === 'google') {
      list.push(...formatGoogleCampaigns(googleCampaigns).map((c) => ({ ...c, label: `${c.name} (Google)` })));
    }
    if (platform === 'all' || platform === 'meta') {
      list.push(...formatMetaCampaigns(metaCampaigns).map((c) => ({ ...c, label: `${c.name} (Meta)` })));
    }
    return list;
  }, [platform, campaignsFromTable, googleCampaigns, metaCampaigns]);

  // Agrupar campañas por plataforma para el desplegable (Google y Meta)
  const campaignOptionsByPlatform = useMemo(() => {
    const google = campaignOptions.filter((c) => c.platform === 'google');
    const meta = campaignOptions.filter((c) => c.platform === 'meta');
    return { google, meta };
  }, [campaignOptions]);

  const loadCampaigns = useCallback(() => {
    if (isGoogleConnected) fetchGoogleCampaigns({ force: false });
    if (isMetaConnected) fetchMetaCampaigns({ force: false });
  }, [isGoogleConnected, isMetaConnected, fetchGoogleCampaigns, fetchMetaCampaigns]);

  const periodLabel = useMemo(() => {
    if (selectedRange === 'CUSTOM' && startDateObj && endDateObj) {
      return `${formatDate(startDateObj, 'full-year-short')} - ${formatDate(endDateObj, 'full-year-short')}`;
    }
    const p = basePeriods.find((x) => x.key === selectedRange);
    return p ? p.label : 'Período';
  }, [selectedRange, startDateObj, endDateObj]);

  const handleApplyPeriodFilters = useCallback(
    (payload) => {
      if (payload?.range) {
        setStartDate(payload.range.startDateStr);
        setEndDate(payload.range.endDateStr);
        setSelectedRange(payload.periodKey ?? 'CUSTOM');
      }
      setIsPeriodModalOpen(false);
    },
    [setStartDate, setEndDate, setSelectedRange]
  );

  const handleSelectPeriod = useCallback(
    (periodKey) => {
      if (periodKey === 'CUSTOM') {
        setIsPeriodModalOpen(true);
        return;
      }
      const p = basePeriods.find((x) => x.key === periodKey);
      if (!p) return;
      const range = calculateDateRange(p.key, p.days ?? 0, '', '');
      if (range?.startDateStr && range?.endDateStr) {
        setStartDate(range.startDateStr);
        setEndDate(range.endDateStr);
        setSelectedRange(periodKey);
      }
    },
    [setStartDate, setEndDate, setSelectedRange]
  );

  const toggleCampaign = useCallback(
    (id) => {
      setSelectedCampaignIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [setSelectedCampaignIds]
  );

  const clearCampaigns = useCallback(() => {
    setSelectedCampaignIds([]);
  }, [setSelectedCampaignIds]);

  // Contenido de filtros para el slot de la barra superior (a la izquierda del brand selector)
  const filtersContent = useMemo(
    () => {
      const isSmallScreen = windowWidth < 1024; // Mobile (<768px) + Tablet (768-1023px) both use drawer

      if (isSmallScreen) {
        // Filter icon now in MobileTopNavBar
        return null;
      }

      // Desktop version: Keep original layout
      return (
      <div
        className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl p-3"
        data-testid="analytics-sticky-filter"
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Período */}
          <CollapsibleFilterGroup
            label="Período"
            value={selectedRange}
            options={basePeriods.filter((p) => p.key !== 'CUSTOM').map((p) => ({ value: p.key, label: p.label }))}
            onChange={handleSelectPeriod}
            getOptionLabel={(v) => (v === 'CUSTOM' ? periodLabel : basePeriods.find((p) => p.key === v)?.label ?? v)}
            customOptions={[
              {
                label:
                  selectedRange === 'CUSTOM' && startDate && endDate
                    ? `${formatDate(startDateObj, 'full-year-short')} - ${formatDate(endDateObj, 'full-year-short')}`
                    : 'Personalizado',
                onClick: () => setIsPeriodModalOpen(true),
              },
            ]}
          />

          {/* Campañas */}
          <div
            className="relative flex items-center gap-2 flex-shrink-0 group"
            ref={campaignsRef}
            onMouseEnter={() => {
              loadCampaigns();
              setCampaignsHovered(true);
            }}
            onMouseLeave={() => setCampaignsHovered(false)}
          >
            <span className="text-xs font-medium text-white/70">Campañas:</span>
            <button
              type="button"
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition min-w-[120px] ${
                selectedCampaignIds.length > 0
                  ? 'bg-white/15 border border-white/30 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <Filter className="w-3.5 h-3.5 text-white/70" />
              <span className="truncate">
                {selectedCampaignIds.length === 0
                  ? 'Todas'
                  : `${selectedCampaignIds.length} seleccionada(s)`}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${campaignsHovered ? 'rotate-180' : ''}`} />
            </button>
            {campaignsHovered && (
              <div className="absolute left-0 top-full pt-1 -mt-1 bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-xl py-2 w-72 max-h-64 overflow-y-auto z-30">
                {campaignOptions.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-white/70">Cargando campañas...</p>
                ) : (
                  <>
                    {selectedCampaignIds.length > 0 && (
                      <button
                        type="button"
                        onClick={clearCampaigns}
                        className="w-full px-4 py-2 text-left text-xs text-white/70 hover:bg-white/10 border-b border-white/10"
                      >
                        Limpiar selección
                      </button>
                    )}
                    {campaignOptionsByPlatform.google.length > 0 && (
                      <>
                        <p className="px-4 py-2 text-xs font-semibold text-white/80 border-b border-white/10 sticky top-0 bg-slate-800/95">
                          Google
                        </p>
                        {campaignOptionsByPlatform.google.map((c) => (
                          <label
                            key={`${c.platform}-${c.id}`}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCampaignIds.includes(c.id)}
                              onChange={() => toggleCampaign(c.id)}
                              className="rounded border-white/30"
                            />
                            <span className="text-sm text-white/90 truncate">{c.label}</span>
                          </label>
                        ))}
                      </>
                    )}
                    {campaignOptionsByPlatform.meta.length > 0 && (
                      <>
                        <p className="px-4 py-2 text-xs font-semibold text-white/80 border-b border-white/10 sticky top-0 bg-slate-800/95">
                          Meta
                        </p>
                        {campaignOptionsByPlatform.meta.map((c) => (
                          <label
                            key={`${c.platform}-${c.id}`}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCampaignIds.includes(c.id)}
                              onChange={() => toggleCampaign(c.id)}
                              className="rounded border-white/30"
                            />
                            <span className="text-sm text-white/90 truncate">{c.label}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Conversiones (filtro CPA/ROAS) */}
          <div
            className="relative flex items-center gap-2 flex-shrink-0 group"
            ref={conversionsRef}
            onMouseEnter={() => setConversionsHovered(true)}
            onMouseLeave={() => setConversionsHovered(false)}
          >
            <span className="text-xs font-medium text-white/70">Conversiones:</span>
            <button
              type="button"
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition min-w-[120px] ${
                selectedConversions.length > 0
                  ? 'bg-white/15 border border-white/30 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <Filter className="w-3.5 h-3.5 text-white/70" />
              <span className="truncate">
                {selectedConversions.length === 0
                  ? 'Todas'
                  : `${selectedConversions.length} seleccionada(s)`}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${conversionsHovered ? 'rotate-180' : ''}`} />
            </button>
            {conversionsHovered && (
              <div className="absolute left-0 top-full pt-1 -mt-1 bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-xl py-2 w-72 max-h-64 overflow-y-auto z-30">
                {conversionOptions.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-white/70">
                    {isGoogleConnected || isMetaConnected
                      ? 'Sin conversiones configuradas o cargando…'
                      : 'Conecta Google o Meta para ver conversiones.'}
                  </p>
                ) : (
                  <>
                    {(selectedConversions || []).length > 0 && (
                      <button
                        type="button"
                        onClick={clearConversions}
                        className="w-full px-4 py-2 text-left text-xs text-white/70 hover:bg-white/10 border-b border-white/10"
                      >
                        Limpiar selección
                      </button>
                    )}
                    {conversionOptionsByPlatform.google.length > 0 && (
                      <>
                        <p className="px-4 py-2 text-xs font-semibold text-white/80 border-b border-white/10 sticky top-0 bg-slate-800/95">
                          Google
                        </p>
                        {conversionOptionsByPlatform.google.map((c) => (
                          <label
                            key={`google-${c.id}`}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isConversionSelected(c)}
                              onChange={() => toggleConversion(c)}
                              className="rounded border-white/30"
                            />
                            <span className="text-sm text-white/90 truncate">{c.name || c.id}</span>
                          </label>
                        ))}
                      </>
                    )}
                    {conversionOptionsByPlatform.meta.length > 0 && (
                      <>
                        <p className="px-4 py-2 text-xs font-semibold text-white/80 border-b border-white/10 sticky top-0 bg-slate-800/95">
                          Meta
                        </p>
                        {conversionOptionsByPlatform.meta.map((c) => (
                          <label
                            key={`meta-${c.id}`}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isConversionSelected(c)}
                              onChange={() => toggleConversion(c)}
                              className="rounded border-white/30"
                            />
                            <span className="text-sm text-white/90 truncate">{c.name || c.id}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
    },
    [
      windowWidth,
      selectedRange,
      handleSelectPeriod,
      periodLabel,
      startDate,
      endDate,
      startDateObj,
      endDateObj,
      campaignsRef,
      campaignsHovered,
      loadCampaigns,
      campaignOptions,
      selectedCampaignIds,
      clearCampaigns,
      campaignOptionsByPlatform,
      conversionsRef,
      conversionsHovered,
      selectedConversions,
      conversionOptions,
      clearConversions,
      toggleCampaign,
      toggleConversion,
      isConversionSelected,
      isGoogleConnected,
      isMetaConnected,
      setIsPeriodModalOpen,
      conversionOptionsByPlatform.google,
      conversionOptionsByPlatform.meta,
    ]
  );

  useEffect(() => {
    registerContent(filtersContent);
    return () => clearContent();
  }, [filtersContent, registerContent, clearContent]);

  return (
    <>
      {/* Popup del selector de fechas (mismo componente que en Analytics20) */}
      <EnhancedDateRangeSelector
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        selectedPeriod={selectedRange}
        onPeriodChange={setSelectedRange}
        customStartDate={startDate}
        customEndDate={endDate}
        onCustomDatesChange={(s, e) => {
          setStartDate(s);
          setEndDate(e);
          setSelectedRange('CUSTOM');
        }}
        isCompareEnabled={false}
        periods={periodsForModal}
        onApplyFilters={handleApplyPeriodFilters}
      />

      {/* Mobile & Tablet filter drawer - shown for widths < 1024px */}
      {windowWidth < 1024 && (
        <MobileFilterDrawer
          isOpen={isMobileFilterOpen}
          onClose={closeMobileFilter}
          filters={[
            {
              key: 'period',
              label: 'Período',
              options: basePeriods.filter(p => p.key !== 'CUSTOM').map(p => ({
                label: p.label,
                value: p.key
              }))
            },
            {
              key: 'campaigns',
              label: 'Campañas',
              options: campaignOptions.map(c => ({
                label: c.label,
                value: c.id,
                count: undefined
              }))
            },
            {
              key: 'conversions',
              label: 'Conversiones',
              options: conversionOptions.map(c => ({
                label: c.name || c.id,
                value: `${c.platform}_${c.id}`,
                count: undefined
              }))
            }
          ]}
          selectedFilters={{
            period: selectedRange,
            campaigns: selectedCampaignIds.length === 1 ? selectedCampaignIds[0] : null,
            conversions: selectedConversions.length === 1 ? `${selectedConversions[0].platform}_${selectedConversions[0].id}` : null
          }}
          onFilterChange={(key, value) => {
            if (key === 'period') {
              handleSelectPeriod(value);
            } else if (key === 'campaigns') {
              setSelectedCampaignIds(value ? [value] : []);
            } else if (key === 'conversions' && value) {
              const [platform, id] = value.split('_');
              const conversion = conversionOptions.find(c => c.platform === platform && String(c.id) === id);
              if (conversion) {
                setSelectedConversions([conversion]);
              }
            }
          }}
          darkMode={true}
        />
      )}
    </>
  );
}
